import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Spin, Tag, Empty, message, Row, Col, Avatar } from 'antd';
import { TeamOutlined, PlusOutlined, LogoutOutlined, CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';

// Tela pos-login quando o admin_user tem >1 times. Cada card representa um
// time; clicar troca o JWT (POST /organizer/teams/switch) e vai pro dashboard.
// Se can_create_new_team, mostra CTA pra criar novo time.
export default function TeamSelector({ onSwitched }) {
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/organizer/teams/my');
      setTeams(r.data.teams || []);
      setCanCreate(!!r.data.can_create_new_team);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao listar times');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const chooseTeam = async (customerId) => {
    setSwitching(customerId);
    try {
      const r = await api.post('/organizer/teams/switch', { customer_id: customerId });
      // Substitui token + user localStorage e faz HARD redirect pra garantir
      // que o app arranca do zero com o token novo (evita dados stale do time
      // anterior aparecerem misturados).
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      localStorage.setItem('active_customer_id', String(customerId));
      if (onSwitched) onSwitched(r.data.user);
      // Detecta hostname: /team-config so existe no admin. Se o organizador
      // caiu aqui pelo hostname da comunidade, redireciona pro admin.
      const host = window.location.hostname;
      if (host === 'comunidade.futezap.com.br') {
        window.location.href = 'https://admin.futezap.com.br/team-config';
      } else {
        window.location.href = '/team-config';
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao entrar no time');
    } finally { setSwitching(null); }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '40px auto', padding: 24 }}>
      <div style={{ marginBottom: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.5 }}>Qual time hoje?</div>
        <div style={{ color: '#666', marginTop: 4 }}>Você é organizador em {teams.length} times. Escolhe um pra entrar.</div>
      </div>

      {teams.length === 0 ? (
        <Empty description="Nenhum time associado" />
      ) : (
        <Row gutter={[16, 16]}>
          {teams.map(t => (
            <Col xs={24} sm={12} md={8} key={t.id}>
              <Card
                hoverable
                onClick={() => chooseTeam(t.id)}
                style={{
                  borderRadius: 16, borderColor: switching === t.id ? '#25D366' : '#eef1f4',
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: 20 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <Avatar
                    size={48}
                    src={absoluteUploadUrl(t.logo_url)}
                    icon={<TeamOutlined />}
                    style={{ backgroundColor: '#128C7E' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                      {t.role === 'OWNER' && <Tag color="gold" icon={<CrownOutlined />}>Dono</Tag>}
                      {t.status !== 'ATIVO' && <Tag color="red">{t.status}</Tag>}
                      {!t.has_group && <Tag>sem grupo</Tag>}
                    </div>
                  </div>
                </div>
                <Button
                  type="primary" block
                  loading={switching === t.id}
                  onClick={(e) => { e.stopPropagation(); chooseTeam(t.id); }}
                  style={{ background: '#25D366', borderColor: '#25D366' }}
                >
                  Entrar
                </Button>
              </Card>
            </Col>
          ))}

          {canCreate && (
            <Col xs={24} sm={12} md={8}>
              <Card
                hoverable
                onClick={() => nav('/organizer/teams/new')}
                style={{
                  borderRadius: 16, borderStyle: 'dashed', borderColor: '#25D366',
                  cursor: 'pointer', height: '100%', display: 'flex', alignItems: 'center',
                }}
                bodyStyle={{ padding: 20, textAlign: 'center', width: '100%' }}
              >
                <PlusOutlined style={{ fontSize: 32, color: '#25D366', marginBottom: 8 }} />
                <div style={{ fontWeight: 700, fontSize: 16 }}>Criar novo time</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Sem trial · cobrança em 30 dias</div>
              </Card>
            </Col>
          )}
        </Row>
      )}

      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <Button type="text" icon={<LogoutOutlined />} onClick={logout}>Sair</Button>
      </div>
    </div>
  );
}
